import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { volunteerAPI, documentAPI } from '../services/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CommunityVolunteerPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('community-engagement');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [myEngagements, setMyEngagements] = useState<any[]>([]);
  const [myDocuments, setMyDocuments] = useState<any[]>([]);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasAccess = user && user.role === 'community_volunteer';

  useEffect(() => {
    if (hasAccess) loadData();
  }, [activeTab, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'my-activities') {
        const [engRes, docRes] = await Promise.all([
          volunteerAPI.getMyEngagements(user.id),
          volunteerAPI.getMyDocuments(user.id)
        ]);
        setMyEngagements(engRes.data || []);
        setMyDocuments(docRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 4000);
  };

  if (!hasAccess) {
    return (
      <div className="volunteer-page">
        <header className="page-header">
          <div className="icon icon-primary">🔒</div>
          <h1>Community Volunteer Access Required</h1>
          <p>Only approved Community Volunteers can access this page</p>
        </header>
        <div className="access-denied">
          <p>This page is restricted to approved Community Volunteers only.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">Staff Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="volunteer-page">
      <header className="page-header">
        <div className="icon icon-primary">🤝</div>
        <h1>Community Volunteer Dashboard</h1>
        <p>Document community engagements and share learning materials</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') || message.includes('submitted') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="volunteer-tabs">
        <div className="tabs-container">
          <button className={`tab-button ${activeTab === 'community-engagement' ? 'active' : ''}`} onClick={() => setActiveTab('community-engagement')}>
            🤝 Document Engagement
          </button>
          <button className={`tab-button ${activeTab === 'learning-materials' ? 'active' : ''}`} onClick={() => setActiveTab('learning-materials')}>
            📚 Share Materials
          </button>
          <button className={`tab-button ${activeTab === 'my-activities' ? 'active' : ''}`} onClick={() => setActiveTab('my-activities')}>
            📋 My Activities
          </button>
        </div>
      </div>

      <div className="volunteer-content">

        {/* ── DOCUMENT ENGAGEMENT ── */}
        {activeTab === 'community-engagement' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">🤝</div>
              <div>
                <h2>Document Community Activity</h2>
                <p>Record your community interactions. Submissions go to your Civic Moderator for approval before becoming public.</p>
              </div>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              formData.append('conducted_by', user.id);
              formData.append('ward_id', user.ward_id || '');
              try {
                const res = await fetch(`${API_BASE}/volunteer/engagements`, {
                  method: 'POST',
                  body: formData,
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const data = await res.json();
                if (res.ok) {
                  showMessage('Activity submitted for civic moderator approval');
                  (e.target as HTMLFormElement).reset();
                } else {
                  showMessage(data.error || 'Failed to submit activity');
                }
              } catch { showMessage('Failed to submit activity'); }
            }}>
              <div className="form-row">
                <input name="title" placeholder="Activity Title" required />
                <select name="engagement_type" required>
                  <option value="">Select Activity Type</option>
                  <option value="community_meeting">Community Meeting</option>
                  <option value="training_session">Training Session</option>
                  <option value="awareness_campaign">Awareness Campaign</option>
                  <option value="material_distribution">Material Distribution</option>
                  <option value="feedback_collection">Feedback Collection</option>
                  <option value="outreach_visit">Outreach Visit</option>
                </select>
              </div>
              <div className="form-row">
                <input name="location" placeholder="Specific Location" required />
                <input name="participants_count" type="number" placeholder="Number of Participants" min="0" />
              </div>
              <div className="form-row">
                <input name="engagement_date" type="datetime-local" required />
                <input name="duration_hours" type="number" step="0.5" placeholder="Duration (hours)" min="0" />
              </div>
              <div className="form-row">
                <textarea name="description" placeholder="Activity Description" rows={3} required />
              </div>
              <div className="form-row">
                <textarea name="outcomes" placeholder="Key Outcomes and Community Response" rows={2} />
              </div>
              <div className="form-row">
                <textarea name="challenges" placeholder="Challenges Encountered" rows={2} />
              </div>
              <div className="form-row">
                <textarea name="recommendations" placeholder="Recommendations and Next Steps" rows={2} />
              </div>
              <div className="file-upload-section">
                <h4>Upload Documentation</h4>
                <div className="upload-types">
                  <div className="upload-type">
                    <label>📷 Photos</label>
                    <input name="attachments" type="file" multiple accept="image/*" />
                  </div>
                  <div className="upload-type">
                    <label>🎥 Videos</label>
                    <input name="attachments" type="file" multiple accept="video/*" />
                  </div>
                  <div className="upload-type">
                    <label>📄 Documents</label>
                    <input name="attachments" type="file" multiple accept=".pdf,.doc,.docx" />
                  </div>
                </div>
                <small>Max 50MB per file. Submission requires civic moderator approval before going public.</small>
              </div>
              <button type="submit" className="btn btn-primary">Submit Activity for Approval</button>
            </form>
          </section>
        )}

        {/* ── SHARE LEARNING MATERIALS ── */}
        {activeTab === 'learning-materials' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📚</div>
              <div>
                <h2>Share Learning Materials</h2>
                <p>Upload educational materials and resources for the community.</p>
              </div>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              formData.append('uploaded_by', user.id);
              formData.append('document_type', 'training');
              formData.append('ward_id', user.ward_id || '');
              try {
                const res = await fetch(`${API_BASE}/admin/documents`, {
                  method: 'POST',
                  body: formData,
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                const data = await res.json();
                if (res.ok) {
                  showMessage('Learning material uploaded successfully');
                  (e.target as HTMLFormElement).reset();
                } else {
                  showMessage(data.error || 'Failed to upload material');
                }
              } catch { showMessage('Failed to upload material'); }
            }}>
              <div className="form-row">
                <input name="title" placeholder="Material Title" required />
                <select name="sector">
                  <option value="">Select Topic Area</option>
                  <option value="Health">Health & Wellness</option>
                  <option value="Education">Education & Literacy</option>
                  <option value="Agriculture">Agriculture & Farming</option>
                  <option value="Water">Water & Sanitation</option>
                  <option value="Infrastructure">Infrastructure & Development</option>
                  <option value="General">Governance & Civic Education</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-row">
                <textarea name="description" placeholder="Description of the learning material" rows={3} required />
              </div>
              <div className="file-upload-section">
                <h4>Upload File</h4>
                <input name="file" type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" required />
                <small>Supported: PDF, DOC, DOCX, PPT, PPTX — Max 50MB</small>
              </div>
              <button type="submit" className="btn btn-primary">Upload Material</button>
            </form>
          </section>
        )}

        {/* ── MY ACTIVITIES ── */}
        {activeTab === 'my-activities' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📋</div>
              <div>
                <h2>My Activities</h2>
                <p>Track, edit and delete your submitted engagements and uploaded materials.</p>
              </div>
            </div>

            {loading ? <div className="loading">Loading your activities...</div> : (
              <>
                {/* Stats */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                    <h3>{myEngagements.length}</h3><p>Engagements</p>
                  </div>
                  <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                    <h3>{myEngagements.reduce((s, e) => s + (e.participants_count || 0), 0)}</h3><p>People Reached</p>
                  </div>
                  <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                    <h3>{myEngagements.reduce((s, e) => s + (e.duration_hours || 0), 0)}h</h3><p>Hours Volunteered</p>
                  </div>
                  <div className="stat-card" style={{ flex: 1, minWidth: '130px' }}>
                    <h3>{myDocuments.length}</h3><p>Materials Shared</p>
                  </div>
                </div>

                {/* Engagements */}
                <h3 style={{ marginBottom: '0.8rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.4rem' }}>🤝 My Engagements</h3>
                {myEngagements.length === 0 ? (
                  <p style={{ color: '#666', marginBottom: '1.5rem' }}>No engagements submitted yet. Go to the “Document Engagement” tab to submit one.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
                    {myEngagements.map(eng => (
                      <div key={eng.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <h4 style={{ margin: 0 }}>{eng.title}</h4>
                            <small style={{ color: '#666' }}>
                              {eng.engagement_type?.replace(/_/g, ' ')} &bull; {eng.location} &bull; {new Date(eng.engagement_date).toLocaleDateString()}
                            </small>
                          </div>
                          <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                            background: eng.approval_status === 'approved' ? '#e8f5e9' : eng.approval_status === 'rejected' ? '#ffebee' : '#fff3e0',
                            color: eng.approval_status === 'approved' ? '#43a047' : eng.approval_status === 'rejected' ? '#e53935' : '#fb8c00' }}>
                            {eng.approval_status === 'approved' ? '✅ Approved & Public' : eng.approval_status === 'rejected' ? '❌ Rejected' : '⏳ Pending Approval'}
                          </span>
                        </div>

                        {eng.description && <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#444' }}>{eng.description}</p>}
                        {eng.outcomes && <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: '#555' }}><strong>Outcomes:</strong> {eng.outcomes}</p>}

                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                          <span>👥 {eng.participants_count} participants</span>
                          <span>⏱️ {eng.duration_hours}h</span>
                          <span>📎 {eng.attachments_count} files</span>
                        </div>

                        {eng.approval_status === 'pending' && (
                          <details style={{ marginTop: '0.8rem' }}>
                            <summary style={{ cursor: 'pointer', color: '#1976d2', fontSize: '0.9rem' }}>✏️ Edit this submission</summary>
                            <form style={{ marginTop: '0.8rem' }} onSubmit={async (e) => {
                              e.preventDefault();
                              const fd = new FormData(e.target as HTMLFormElement);
                              try {
                                await volunteerAPI.updateEngagement(eng.id, {
                                  title: fd.get('title'),
                                  description: fd.get('description'),
                                  location: fd.get('location'),
                                  participants_count: fd.get('participants_count'),
                                  duration_hours: fd.get('duration_hours'),
                                  outcomes: fd.get('outcomes'),
                                  challenges: fd.get('challenges'),
                                });
                                showMessage('Engagement updated successfully');
                                loadData();
                              } catch { showMessage('Failed to update engagement'); }
                            }}>
                              <div className="form-row">
                                <input name="title" defaultValue={eng.title} placeholder="Title" required />
                                <input name="location" defaultValue={eng.location} placeholder="Location" />
                              </div>
                              <div className="form-row">
                                <input name="participants_count" type="number" defaultValue={eng.participants_count} placeholder="Participants" />
                                <input name="duration_hours" type="number" step="0.5" defaultValue={eng.duration_hours} placeholder="Duration (h)" />
                              </div>
                              <div className="form-row">
                                <textarea name="description" defaultValue={eng.description} placeholder="Description" rows={2} />
                              </div>
                              <div className="form-row">
                                <textarea name="outcomes" defaultValue={eng.outcomes} placeholder="Outcomes" rows={2} />
                              </div>
                              <div className="form-row">
                                <textarea name="challenges" defaultValue={eng.challenges} placeholder="Challenges" rows={2} />
                              </div>
                              <button type="submit" className="btn btn-primary">Save Changes</button>
                            </form>
                          </details>
                        )}

                        {(eng.approval_status === 'pending' || eng.approval_status === 'rejected') && (
                          <button className="btn btn-danger" style={{ marginTop: '0.8rem', fontSize: '0.85rem' }}
                            onClick={async () => {
                              if (!window.confirm('Delete this engagement submission?')) return;
                              try {
                                await volunteerAPI.deleteEngagement(eng.id);
                                showMessage('Engagement deleted');
                                loadData();
                              } catch { showMessage('Failed to delete engagement'); }
                            }}>🗑️ Delete Submission
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Documents */}
                <h3 style={{ marginBottom: '0.8rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.4rem' }}>📚 My Uploaded Materials</h3>
                {myDocuments.length === 0 ? (
                  <p style={{ color: '#666' }}>No materials uploaded yet. Go to the “Share Materials” tab to upload one.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {myDocuments.map(doc => (
                      <div key={doc.id} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <h4 style={{ margin: 0 }}>{doc.title}</h4>
                            <small style={{ color: '#666' }}>{doc.sector} &bull; {doc.file_format} &bull; {new Date(doc.created_at).toLocaleDateString()}</small>
                            {doc.description && <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: '#555' }}>{doc.description}</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                              background: doc.approval_status === 'approved' ? '#e8f5e9' : '#fff3e0',
                              color: doc.approval_status === 'approved' ? '#43a047' : '#fb8c00' }}>
                              {doc.approval_status === 'approved' ? '✅ Public' : '⏳ Pending Approval'}
                            </span>
                            <a href={`http://localhost:5000${doc.file_url}`} target="_blank" rel="noopener noreferrer"
                              className="btn btn-secondary" style={{ fontSize: '0.85rem' }}>📥 View</a>
                            <button className="btn btn-danger" style={{ fontSize: '0.85rem' }}
                              onClick={async () => {
                                if (!window.confirm('Delete this material?')) return;
                                try {
                                  await volunteerAPI.deleteDocument(doc.id);
                                  showMessage('Material deleted');
                                  loadData();
                                } catch { showMessage('Failed to delete material'); }
                              }}>🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default CommunityVolunteerPage;
